import { Box, type DrawerProps, Grid, IconButton, Stack, Typography, styled, useTheme } from "@mui/material";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { Drawer, DrawerContent } from "Components/Analyses/_Shared/Drawer";
import { Button } from "Components/_Shared/Buttons/Button";
import { ReactComponent as CloseIcon } from "Components/_Shared/Icons/Assets/close.svg";
import { type ExpenseLine, ExpenseLineExpenseType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type FC, useContext, useRef, useState } from "react";
import { ConsumptionExpenseForm } from "../Forms/ConsumptionExpenseForm";
import { FinancialProductExpenseForm } from "../Forms/FinancialProductExpenseForm";
import { HousingExpenseForm } from "../Forms/HousingExpenseForm";
import { InsuranceExpenseForm } from "../Forms/InsuranceExpenseForm";
import { LifestyleExpenseForm } from "../Forms/LifestyleExpenseForm";
import { LoanExpenseForm } from "../Forms/LoanExpenseForm";
import { OtherExpenseForm } from "../Forms/OtherExpenseForm";
import { EXPENSE_TYPE_ORDER } from "../expenseTypes";
import { ExpenseTypeTile } from "./ExpenseTypeTile";

type Props = {
  onClose: () => void;
  expense: ExpenseLine;
} & DrawerProps;

export type ExpenseFormHandle = {
  readonly submitForm: () => void;
  readonly deleteExpense: () => void;
};

export const EditExpenseDrawer: FC<Props> = ({ onClose, expense, ...rest }) => {
  const { t } = useResource();
  const [expenseType, setExpenseType] = useState<ExpenseLineExpenseType>(expense.expenseType);
  const { readOnly } = useContext(AnalysisDetailContext);

  const housingExpenseFormRef = useRef<ExpenseFormHandle>(null);
  const consumptionExpenseFormRef = useRef<ExpenseFormHandle>(null);
  const lifestyleExpenseFormRef = useRef<ExpenseFormHandle>(null);
  const loanExpenseFormRef = useRef<ExpenseFormHandle>(null);
  const insuranceExpenseFormRef = useRef<ExpenseFormHandle>(null);
  const financialProductExpenseFormRef = useRef<ExpenseFormHandle>(null);
  const otherExpenseFormRef = useRef<ExpenseFormHandle>(null);

  return (
    <Drawer {...rest} onClose={onClose}>
      <Box display="flex" justifyContent="space-between" alignItems="center" p={Spacing.XXL}>
        <Typography variant="h2">{t(Resources.AnalysisDetail.Budget.Expenses.Form.Buttons.Edit)}</Typography>

        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DrawerContent>
        <Box>
          <Box px={Spacing.XXL} mb={Spacing.XXL}>
            <Stack spacing={Spacing.XL}>
              <Typography variant="subtitle1" fontWeight={600}>
                {t(Resources.AnalysisDetail.Budget.Expenses.ExpenseType)}
              </Typography>

              <Grid container spacing={Spacing.M}>
                {EXPENSE_TYPE_ORDER.map((value) => {
                  return (
                    <Grid key={value} size={3}>
                      <ExpenseTypeTile
                        active={value === expenseType}
                        disabled={value !== expenseType}
                        expenseType={value}
                        onExpenseSelect={(type) => {
                          setExpenseType(type);
                        }}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Stack>
          </Box>

          <Box px={Spacing.XXL} mb={Spacing.XXL}>
            {expenseType === ExpenseLineExpenseType.housing && <HousingExpenseForm onClose={onClose} ref={housingExpenseFormRef} />}
            {expenseType === ExpenseLineExpenseType.consumption && (
              <ConsumptionExpenseForm onClose={onClose} ref={consumptionExpenseFormRef} />
            )}
            {expenseType === ExpenseLineExpenseType.life_style && <LifestyleExpenseForm onClose={onClose} ref={lifestyleExpenseFormRef} />}
            {expenseType === ExpenseLineExpenseType.loan && <LoanExpenseForm onClose={onClose} ref={loanExpenseFormRef} />}
            {expenseType === ExpenseLineExpenseType.insurance && <InsuranceExpenseForm onClose={onClose} ref={insuranceExpenseFormRef} />}
            {expenseType === ExpenseLineExpenseType["financial-product"] && (
              <FinancialProductExpenseForm onClose={onClose} ref={financialProductExpenseFormRef} />
            )}
            {expenseType === ExpenseLineExpenseType.other && <OtherExpenseForm onClose={onClose} ref={otherExpenseFormRef} />}
          </Box>
        </Box>
      </DrawerContent>

      <Box display="flex" flexDirection="row" justifyContent="space-between" py={Spacing.XXL} px={Spacing.XXL}>
        <Button
          disabled={readOnly}
          variant="text"
          color="error"
          onClick={() => {
            if (expenseType === ExpenseLineExpenseType.housing) {
              housingExpenseFormRef.current?.deleteExpense();
            }
            if (expenseType === ExpenseLineExpenseType.consumption) {
              consumptionExpenseFormRef.current?.deleteExpense();
            }
            if (expenseType === ExpenseLineExpenseType.life_style) {
              lifestyleExpenseFormRef.current?.deleteExpense();
            }
            if (expenseType === ExpenseLineExpenseType.loan) {
              loanExpenseFormRef.current?.deleteExpense();
            }
            if (expenseType === ExpenseLineExpenseType.insurance) {
              insuranceExpenseFormRef.current?.deleteExpense();
            }
            if (expenseType === ExpenseLineExpenseType["financial-product"]) {
              financialProductExpenseFormRef.current?.deleteExpense();
            }
            if (expenseType === ExpenseLineExpenseType.other) {
              otherExpenseFormRef.current?.deleteExpense();
            }
          }}
        >
          {t(Resources.Common.Delete)}
        </Button>
        <Box display={"flex"} flexDirection="row">
          <Button
            onClick={onClose}
            color="secondary"
            sx={{
              marginRight: Spacing.XL,
            }}
          >
            {t(Resources.Common.Close)}
          </Button>

          <Button
            type="submit"
            disabled={readOnly}
            onClick={() => {
              if (expenseType === ExpenseLineExpenseType.housing) {
                housingExpenseFormRef.current?.submitForm();
              }
              if (expenseType === ExpenseLineExpenseType.consumption) {
                consumptionExpenseFormRef.current?.submitForm();
              }
              if (expenseType === ExpenseLineExpenseType.life_style) {
                lifestyleExpenseFormRef.current?.submitForm();
              }
              if (expenseType === ExpenseLineExpenseType.loan) {
                loanExpenseFormRef.current?.submitForm();
              }
              if (expenseType === ExpenseLineExpenseType.insurance) {
                insuranceExpenseFormRef.current?.submitForm();
              }
              if (expenseType === ExpenseLineExpenseType["financial-product"]) {
                financialProductExpenseFormRef.current?.submitForm();
              }
              if (expenseType === ExpenseLineExpenseType.other) {
                otherExpenseFormRef.current?.submitForm();
              }
            }}
          >
            {t(Resources.Common.Save)}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
